// api/sessions.ts — student-side sessions API typed wrappers.
//
// W3 Phase A: `recent()` — drawer Cronologia list.
// W3 Phase B (Session 2): `getChiediMessages()` — single-thread Chiedi resume read.
//   Consumed by Session 3 (Phase C) to hydrate the chat thread on Cronologia
//   click. Ships now so Session 3 only does the FE wiring.
// W3 Phase C (Session 3): `getSimulaSummary()` — read-only post-mortem fetch
//   consumed by SimulaPostMortemScreen at /post-mortem/simula/:id.
//   (`getAllenaSummary()` was removed in V29 S8 with the retired Allena
//   post-mortem surface; its route now redirects to Progressi.)

import { apiJson } from './client';
import type { ProfFigureCommand, ProfFigureSnapshot } from '../types/prof-figure';

export type RecentSessionMode =
  | 'allena'
  | 'simula_cieca'
  | 'simula_guidata'
  | 'simula_difficile'
  | 'chiedi';

export interface RecentSession {
  id:         string;
  mode:       RecentSessionMode;
  started_at: string;
  ended_at:   string | null;
  /** Pre-rendered Italian "tu" summary line. Render verbatim. */
  summary:    string;
}

export interface RecentSessionsPayload {
  sessions: RecentSession[];
}

export type ChiediMessageRole = 'user' | 'assistant';

export interface ChiediMessage {
  id:         string;
  role:       ChiediMessageRole;
  text:       string;
  /** Reserved for forward-compat — Chiedi citation surfacing arrives in a
   *  later workstream. Render-defensive: treat undefined/empty as "none". */
  citations:  unknown[];
  created_at: string;
}

export interface ChiediMessagesPayload {
  messages:  ChiediMessage[];
  /** Optional during a rolling deployment; a missing value means no known figure. */
  figureContext?: ProfFigureSnapshot;
  /** True when the thread has more than 200 turns; v2 will paginate. */
  truncated: boolean;
}

// ---------------------------------------------------------------------------
// W3 Phase C — Post-mortem summary payloads
// ---------------------------------------------------------------------------

export type SimulaPostMortemMode =
  | 'simula_cieca'
  | 'simula_guidata'
  | 'simula_difficile';

export interface SimulaSummaryQuestion {
  questionId:    string | null;
  principleId:   string | null;
  studentAnswer: boolean | null;
  correctAnswer: boolean | null;
  outcome:       string | null;
  createdAt:     string;
}

export interface SimulaSummaryResult {
  totalQuestions:       number;
  correctCount:         number;
  passed:               boolean;
  isBlind:              boolean;
  isAdversarial:        boolean;
  /** JSONB blob — opaque to the FE for v1; we don't render per-principle yet. */
  perPrincipleOutcomes: unknown;
  completedAt:          string;
}

export interface SimulaSummaryPayload {
  sessionId:  string;
  mode:       SimulaPostMortemMode;
  startedAt:  string;
  endedAt:    string | null;
  /** null when the student aborted mid-session (no simulation_results row). */
  simulation: SimulaSummaryResult | null;
  questions:  SimulaSummaryQuestion[];
  truncated:  boolean;
}

export const sessionsApi = {
  updateFigureContext(sessionId: string, command: ProfFigureCommand): Promise<{ figureContext: ProfFigureSnapshot }> {
    return apiJson(`/api/sessions/${encodeURIComponent(sessionId)}/figure-context`, {
      method: 'PATCH',
      body: JSON.stringify(command),
      signal: AbortSignal.timeout(15_000),
    });
  },
  /**
   * Fetch the calling student's most-recent sessions across all modes.
   *
   * @param limit  1..50 (server clamps; default 20).
   */
  async recent(limit: number = 20): Promise<RecentSessionsPayload> {
    const qs = `?limit=${encodeURIComponent(String(limit))}`;
    return apiJson<RecentSessionsPayload>(`/api/sessions/recent${qs}`);
  },

  /**
   * Fetch the full message history for a Chiedi thread. Cross-student
   * attempts return 404 (info-leak-resistant). Use to hydrate the chat
   * store when a student clicks a Chiedi item in the drawer Cronologia.
   *
   * @param sessionId  uuid of the sessions row (mode='chiedi').
   */
  async getChiediMessages(sessionId: string): Promise<ChiediMessagesPayload> {
    return apiJson<ChiediMessagesPayload>(
      `/api/sessions/${encodeURIComponent(sessionId)}/chiedi-messages`,
    );
  },

  /**
   * Fetch the post-mortem summary for a single Simula session. Cross-student
   * or wrong-mode requests return 404. Used by SimulaPostMortemScreen.
   *
   * @param sessionId  uuid of the sessions row (mode IN simula_cieca |
   *                   simula_guidata | simula_difficile).
   */
  async getSimulaSummary(sessionId: string): Promise<SimulaSummaryPayload> {
    return apiJson<SimulaSummaryPayload>(
      `/api/sessions/${encodeURIComponent(sessionId)}/simula-summary`,
    );
  },
};
